import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";

/**
 * Returns the base URL of the tenant selector app in Capacitor.
 * Android uses https://localhost (androidScheme: "https").
 * iOS uses capacitor://localhost (default iOS scheme).
 */
function getSelectorBaseUrl(): string {
  if (Capacitor.getPlatform() === "ios") {
    return "capacitor://localhost/";
  }
  return "https://localhost/";
}

/**
 * Detects if the application is running in a Capacitor native context.
 * This indicates the app was opened from the tenant selector mobile app.
 *
 * Safe for server-side rendering - returns false on server.
 */
export function isOpenedFromCapacitor(): boolean {
  // Server-side always returns false
  if (typeof window === "undefined") {
    return false;
  }

  // Check if running on native platform (iOS/Android)
  return Capacitor.isNativePlatform();
}

/**
 * Navigate back to the tenant selector app.
 * Uses the platform-specific local URL scheme.
 */
export function navigateToTenantSelector({ resumeSession = false }: { resumeSession?: boolean } = {}): void {
  if (typeof window === "undefined" || !Capacitor.isNativePlatform()) {
    return;
  }

  const url = new URL(getSelectorBaseUrl());
  url.searchParams.set("resumeSession", String(resumeSession));
  window.location.replace(url.toString());
}

/** Clear the native account before clearing the portal session on explicit logout. */
export async function signOutNativeSession(): Promise<void> {
  if (!isOpenedFromCapacitor()) return;
  await FirebaseAuthentication.signOut();
}
